import type { ReactNode } from "react";
import type { IconProps } from "./types";

/** The default grid. Icons with a large variant pass the 24 one instead. */
export const ICON_VIEW_BOX = "0 0 16 16";

type IconBaseProps = IconProps & {
	/** Path/shape elements for the icon, supplied by each generated component. */
	children: ReactNode;
	/**
	 * The grid THIS drawing was authored on. Generated components pass it;
	 * nothing else should need to. It is not part of IconProps on purpose — a
	 * caller choosing a viewBox would be choosing it independently of the paths
	 * underneath, which can only ever be wrong.
	 */
	viewBox?: string;
};

/**
 * The single runtime primitive behind every icon. Owns the shared grid, sizing,
 * color model, and accessibility defaults so individual icons stay dumb payloads.
 *
 * `weight` is destructured out (and ignored for now) so it never leaks onto the
 * DOM node; it exists purely to keep the API forward-compatible.
 */
export function IconBase({
	size = 16,
	weight: _weight,
	className,
	children,
	viewBox = ICON_VIEW_BOX,
	...rest
}: IconBaseProps) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox={viewBox}
			width={size}
			height={size}
			fill="currentColor"
			className={className}
			// Decorative by default; pass an `aria-label` to promote it to an image.
			aria-hidden={rest["aria-label"] ? undefined : true}
			role={rest["aria-label"] ? "img" : undefined}
			{...rest}
		>
			{children}
		</svg>
	);
}
